import { useEffect, useRef, useState } from 'react';
import { api, errorText } from '../lib/api';
import { fmtDateTime } from '../lib/util';

export function Exports({ campaignId }: { campaignId: string }) {
  const [sessions, setSessions] = useState<any[]>([]);
  const [sid, setSid] = useState('');
  const [playerSafe, setPlayerSafe] = useState(false);
  const [narrative, setNarrative] = useState(true);
  const [preview, setPreview] = useState<any>(null);
  const [recap, setRecap] = useState<any>(null);
  const [err, setErr] = useState('');
  const poll = useRef<number>(0);
  useEffect(() => {
    api(`/api/campaigns/${campaignId}/dashboard`)
      .then((d: any) => {
        setSessions(d.sessions);
        setSid(d.sessions[0]?.id ?? '');
      })
      .catch((e) => setErr(errorText(e)));
    return () => clearTimeout(poll.current);
  }, [campaignId]);
  const qs = `playerSafe=${playerSafe ? 1 : 0}&narrative=${narrative ? 1 : 0}`;
  const loadPreview = async () => {
    if (!sid) return;
    try {
      setPreview(await api(`/api/campaigns/${campaignId}/sessions/${sid}/export/preview?${qs}`));
      setRecap(await api(`/api/campaigns/${campaignId}/sessions/${sid}/recaps/latest?playerSafe=${playerSafe ? 1 : 0}`));
      setErr('');
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void loadPreview();
  }, [sid, playerSafe, narrative]);
  const generate = async () => {
    try {
      await api(`/api/campaigns/${campaignId}/sessions/${sid}/recaps`, { body: { playerSafe } });
      const tick = async () => {
        const r = await api(`/api/campaigns/${campaignId}/sessions/${sid}/recaps/latest?playerSafe=${playerSafe ? 1 : 0}`);
        setRecap(r);
        if (r?.status === 'queued') poll.current = window.setTimeout(tick, 2000);
        else void loadPreview();
      };
      void tick();
    } catch (e) {
      setErr(errorText(e));
    }
  };
  const base = `/api/campaigns/${campaignId}/sessions/${sid}/export`;
  return (
    <div className="page">
      <h1>Export & backup</h1>
      {err && <p className="error">{err}</p>}
      <section className="card">
        <h2>Session recap</h2>
        <div className="row wrap">
          <label>
            Session{' '}
            <select value={sid} onChange={(e) => setSid(e.target.value)}>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  Session {s.number}
                  {s.title ? ` — ${s.title}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="check">
            <input type="checkbox" checked={playerSafe} onChange={(e) => setPlayerSafe(e.target.checked)} /> Player-safe (no GM secrets)
          </label>
          <label className="check">
            <input type="checkbox" checked={narrative} onChange={(e) => setNarrative(e.target.checked)} /> Include narrative recap
          </label>
        </div>
        <div className="row wrap">
          <button className="btn" onClick={() => void generate()} disabled={!sid}>
            Generate {playerSafe ? 'player-safe ' : ''}narrative recap
          </button>
          {recap && (
            <span className="muted small">
              {recap.status === 'queued' ? 'Waiting for the AI service…' : recap.status === 'failed' ? `Recap failed: ${recap.error}` : `Narrative from ${fmtDateTime(recap.created_at)}`}
            </span>
          )}
        </div>
        <p className="muted small">The narrative recap only uses events in your notes; every paragraph is tied to its source notes. Review it before sharing.</p>
        {preview && (
          <>
            {preview.doc.warnings.length > 0 && (
              <div className="notice">
                {preview.doc.warnings.map((w: string) => (
                  <p key={w}>⚠ {w}</p>
                ))}
              </div>
            )}
            {playerSafe && (
              <p className="muted small">
                Excluded as GM-only or secret-derived: {preview.doc.excluded.noteItems} organized note(s), {preview.doc.excluded.entries} original note(s), {preview.doc.excluded.entities} record(s).
              </p>
            )}
            <article className="preview" aria-label="Export preview">
              <h2>{preview.doc.title}</h2>
              <p className="muted">{preview.doc.subtitle}</p>
              {preview.doc.narrative?.length > 0 && (
                <>
                  <h3>Recap</h3>
                  {preview.doc.narrative.map((p: string, i: number) => (
                    <p key={i}>{p}</p>
                  ))}
                </>
              )}
              {preview.doc.sections.map((s: any) => (
                <section key={s.heading}>
                  <h3>{s.heading}</h3>
                  <ul>
                    {s.items.map((i: any, k: number) => (
                      <li key={k}>
                        {i.text}
                        {i.note && <em className="muted"> ({i.note})</em>}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </article>
            <div className="row wrap">
              <a className="btn primary" href={`${base}.pdf?${qs}`}>
                Download PDF
              </a>
              <a className="btn" href={`${base}.docx?${qs}`}>
                Download DOCX
              </a>
              <a className="btn" href={`${base}.md?${qs}`}>
                Download Markdown
              </a>
            </div>
          </>
        )}
        <p>
          <a href={`/api/campaigns/${campaignId}/records.md`}>Download all campaign records as Markdown</a>
        </p>
      </section>
      <Backups campaignId={campaignId} />
    </div>
  );
}

function Backups({ campaignId }: { campaignId: string }) {
  const [list, setList] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const load = () => api<any[]>('/api/backups').then(setList).catch(() => undefined);
  useEffect(() => {
    void load();
  }, []);
  const run = async (fn: () => Promise<any>, done: (r: any) => string) => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      setMsg(done(await fn()));
      void load();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <h2>Backups</h2>
      <p className="muted small">A campaign backup contains original notes, records, attachments, sources and history, with a versioned manifest and checksums. Restoring always creates a new campaign; nothing is overwritten.</p>
      <div className="row wrap">
        <button className="btn primary" disabled={busy} onClick={() => void run(() => api(`/api/campaigns/${campaignId}/backup`, { body: {} }), (r) => `Created ${r.name}`)}>
          Back up this campaign
        </button>
        <button className="btn" disabled={busy} onClick={() => void run(() => api('/api/backups/full', { body: {} }), (r) => `Created ${r.name}`)}>
          Full backup (all campaigns)
        </button>
      </div>
      <form
        className="row wrap"
        onSubmit={(e) => {
          e.preventDefault();
          const input = (e.target as HTMLFormElement).elements.namedItem('backup') as HTMLInputElement;
          const file = input.files?.[0];
          if (!file) return;
          const form = new FormData();
          form.append('file', file);
          void run(
            () => api('/api/backups/restore', { form }),
            (r) => `Restored as “${r.name}”.`,
          );
        }}
      >
        <label>
          Restore a campaign backup <input type="file" name="backup" accept=".gz,.tgz,application/gzip" />
        </label>
        <button className="btn" disabled={busy}>
          Restore as new campaign
        </button>
      </form>
      {msg && <p className="ok">{msg}</p>}
      {err && <p className="error">{err}</p>}
      {list.length > 0 && (
        <ul className="plain">
          {list.map((b) => (
            <li key={b.name}>
              <a href={`/api/backups/${encodeURIComponent(b.name)}`}>{b.name}</a> <span className="muted small">{(b.size / 1024).toFixed(0)} KB · {fmtDateTime(b.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
